import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { cn } from '@/lib/utils'
import { Logo } from '../brand/Logo'

interface AccountTopBarProps {
  /** Where the logo leads; unlinked without one. */
  home?: { href: string; label: string }
  /** Before the logo, e.g. the button that opens the side nav on phones. */
  menuButton?: ReactNode
  /** The customer, straight after the logo. */
  org?: ReactNode
  /** The products, centred on the bar; hidden below md unless tabsOnPhones. */
  tabs?: ReactNode
  /** Keeps the products on a phone's bar, where nothing else switches between them. */
  tabsOnPhones?: boolean
  /** Page actions, before the profile. */
  actions?: ReactNode
  /** The signed-in person's menu, at the far right. */
  profile?: ReactNode
}

/** Every Cai app's signed-in top bar: the customer on the left, the products centred, the person on the right. */
export function AccountTopBar({
  home,
  menuButton,
  org,
  tabs,
  tabsOnPhones,
  actions,
  profile,
}: AccountTopBarProps) {
  const logo = <Logo symbolOnly theme="black" width={32} />

  return (
    // z-20: below the apps' drawers and dialogs (z-50), above their side nav (z-10).
    <header
      className={cn(
        'border-ink-100 sticky top-0 z-20 shrink-0 border-b bg-white px-5.5 md:h-16',
        !tabsOnPhones && 'h-14'
      )}
    >
      {/* Three tracks so the products keep their own room and the sides truncate rather than run under them. */}
      <div
        className={cn(
          'flex items-center md:grid md:h-full md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:gap-4.5',
          tabsOnPhones ? 'flex-wrap gap-x-4.5' : 'h-full gap-4.5'
        )}
      >
        {/* On a phone the customer's name gives way first. */}
        <div className={cn('flex min-w-0 flex-1 items-center gap-4.5', tabsOnPhones && 'h-14 md:h-auto')}>
          {menuButton}
          <div className="flex min-w-0 items-center gap-4">
            {home ? (
              <Link to={home.href} aria-label={home.label} className="inline-flex shrink-0 leading-0">
                {logo}
              </Link>
            ) : (
              <span className="inline-flex shrink-0 leading-0">{logo}</span>
            )}
            {org}
          </div>
        </div>
        {tabs ? (
          <div
            className={cn(
              'h-full items-center md:flex',
              // A phone's bar has no room for them beside the customer, so they wrap onto a row of their own.
              // Auto margins centre the row yet let a too-wide one scroll from its first tab instead of clipping.
              tabsOnPhones
                ? 'border-ink-100 no-scrollbar order-last -mx-5.5 flex h-10 basis-[calc(100%+2.75rem)] overflow-x-auto border-t *:mx-auto md:order-none md:mx-0 md:h-full md:basis-auto md:overflow-visible md:border-t-0'
                : 'hidden'
            )}
          >
            {tabs}
          </div>
        ) : (
          <div />
        )}
        {/* The profile keeps its room; wide actions (staff search) narrow within a capped share of a phone's bar. */}
        <div
          className={cn(
            'ml-auto flex shrink-0 items-center justify-end gap-4.5 max-md:max-w-[70%]',
            tabsOnPhones && 'h-14 md:h-auto'
          )}
        >
          {actions && <div className="flex min-w-0 items-center justify-end gap-4.5">{actions}</div>}
          {profile && <div className="shrink-0">{profile}</div>}
        </div>
      </div>
    </header>
  )
}
